// lazy-screen — screens arrive as their own chunks (F-08).
//
// AppShell used to import all 18 screens statically, so every reader
// downloaded every screen. The service registry already decided which
// ones were REACHABLE; it just could not stop them being DOWNLOADED.
// 217 kB of the 835 kB entry chunk was screens behind a service that is
// off in production.
//
// ─── The part that matters is the failure path ──────────────────────
//
// Splitting a bundle trades one guaranteed cost (a bigger download) for
// a new failure mode (a fetch that can fail after the app is running).
// A rejected `import()` throws during render, and with nothing to catch
// it React unmounts the whole tree — a white screen, on a click that
// used to work. That is strictly worse than the problem being solved,
// so the boundary below is not decoration:
//
//   - three attempts (the first plus two retries), backing off, because
//     the common failure is a phone changing cell or a deploy swapping
//     the file out from under an open tab;
//   - then a message with a Refresh button. Reloading is the honest fix
//     for the deploy case: the new index.html names the new chunks.
//
// Never a white screen, and never a spinner that outlives its welcome —
// the loading line waits 150 ms before appearing, so a chunk that
// arrives quickly (the normal case, same origin, warm connection)
// renders no intermediate state at all.

import React, { Suspense, useEffect, useState } from 'react';
import type { ThemeTokens } from '@nayto/theme';

const RETRIES = 2;
const BACKOFF_MS = 250;
/** How long a chunk may take before the reader is told anything. */
const QUIET_MS = 150;

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * `React.lazy` for a screen exported under a name, with retries.
 *
 * Note what is NOT here: a cache-busting query on the retry. The import
 * specifier is what lets the bundler find the chunk, so it cannot be
 * made dynamic. A browser that caches the rejection will fail all three
 * attempts and land on the Refresh button, which is why that button is
 * the real recovery path rather than a courtesy.
 */
export function lazyScreen<M, K extends keyof M>(
  load: () => Promise<M>,
  name: K,
): React.LazyExoticComponent<M[K] extends React.ComponentType<infer _P> ? M[K] : never> {
  return React.lazy(async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= RETRIES; attempt++) {
      try {
        const mod = await load();
        return { default: mod[name] as never };
      } catch (err) {
        lastError = err;
        if (attempt < RETRIES) await delay(BACKOFF_MS * 2 ** attempt);
      }
    }
    throw lastError;
  }) as never;
}

// ─── The boundary ───────────────────────────────────────────────────

interface ScreenBoundaryProps {
  children: React.ReactNode;
  theme: ThemeTokens;
  t: (key: string) => string;
}

interface BoundaryState {
  failed: boolean;
}

/**
 * Catches a chunk that would not load and offers the one thing that
 * reliably fixes it.
 *
 * Mounted with `key={route}` by AppShell so that navigating away from a
 * broken screen resets it — otherwise one failed chunk would poison
 * every subsequent navigation.
 */
class ChunkErrorBoundary extends React.Component<ScreenBoundaryProps, BoundaryState> {
  override state: BoundaryState = { failed: false };

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    const { theme, t } = this.props;
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, marginBottom: 6 }}>
          {t('scrFailedTitle')}
        </div>
        <div
          style={{
            fontSize: 12,
            color: theme.textMuted,
            marginBottom: 14,
            lineHeight: 1.5,
            maxWidth: 340,
            marginInline: 'auto',
          }}
        >
          {t('scrFailedHint')}
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 18px',
            fontSize: 13,
            fontWeight: 600,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            background: theme.surface,
            color: theme.text,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {t('scrRefresh')}
        </button>
      </div>
    );
  }
}

/** Nothing for the first 150 ms, then a muted line. Most chunks land
 * inside that window and the reader sees no flicker at all. */
function QuietLoading({ theme, t }: { theme: ThemeTokens; t: (key: string) => string }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShow(true), QUIET_MS);
    return () => clearTimeout(id);
  }, []);
  return (
    <div style={{ padding: 32, textAlign: 'center', minHeight: 120 }}>
      {show && <span style={{ fontSize: 12, color: theme.textSubtle }}>{t('scrLoading')}</span>}
    </div>
  );
}

/** Suspense + the error boundary, in the order that makes a failed chunk
 * land on the message rather than on nothing. */
export function ScreenBoundary({ children, theme, t }: ScreenBoundaryProps) {
  return (
    <ChunkErrorBoundary theme={theme} t={t}>
      <Suspense fallback={<QuietLoading theme={theme} t={t} />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}
